import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';

export default function TopProductsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": "Top Kakobuy Spreadsheet Products Trending in June 2026",
          "description": "Discover the hottest trending products across all 14 Kakobuy Spreadsheet categories. See what the community is buying on KaSpreadsheet this month.",
          "author": { "@type": "Organization", "name": "Kakobuy Research Team" },
          "datePublished": "2026-06-24",
          "dateModified": "2026-06-25",
          "publisher": { "@type": "Organization", "name": "Kakobuy Spreadsheet", "url": "https://kakobuyspreadsheet.bar" }
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://kakobuyspreadsheet.bar" },
            { "@type": "ListItem", "position": 2, "name": "Top Kakobuy Spreadsheet Products 2026", "item": "https://kakobuyspreadsheet.bar/top-kakobuy-spreadsheet-products-2026" }
          ]
        })}
      </script>

      <Navbar />
      <article className="min-h-screen bg-background-50">
        <div className="max-w-[900px] mx-auto px-6 md:px-12 pt-24 pb-4">
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm text-foreground-500">
            <Link to="/" className="hover:text-foreground-950 transition-colors font-medium">Home</Link>
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-s-line"></i></span>
            <span className="text-foreground-800 font-medium">Top Products 2026</span>
          </nav>
        </div>

        <header className="max-w-[900px] mx-auto px-6 md:px-12 pt-6 pb-10">
          <span className="inline-block px-3 py-1 bg-accent-100 text-foreground-950 text-xs font-semibold rounded-md mb-4 whitespace-nowrap">Trending</span>
          <h1 className="text-foreground-950 text-3xl md:text-4xl lg:text-5xl font-black font-heading leading-[1.15] mb-6">
            Top Kakobuy Spreadsheet Products Trending in June 2026
          </h1>
          <p className="text-foreground-600 text-lg leading-relaxed mb-8 max-w-[750px]">
            Every month, certain products surge in popularity across the <strong>Kakobuy Spreadsheet</strong>. This June, we are seeing strong trends in lightweight outerwear, designer sneakers, and minimalist accessories. Here is a curated look at what the community is loving right now on KaSpreadsheet.
          </p>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 p-5 bg-background-100 rounded-2xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-accent-200 flex items-center justify-center">
                <span className="text-foreground-800 font-bold text-lg">K</span>
              </div>
              <div>
                <p className="text-foreground-950 font-semibold text-sm">Kakobuy Research Team</p>
                <p className="text-foreground-500 text-xs">Updated: June 25, 2026</p>
              </div>
            </div>
            <div className="hidden sm:block w-px h-8 bg-foreground-200"></div>
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1 text-xs text-foreground-500"><span className="w-3 h-3 flex items-center justify-center text-accent-600"><i className="ri-fire-fill"></i></span>Trending Now</span>
              <span className="flex items-center gap-1 text-xs text-foreground-500"><span className="w-3 h-3 flex items-center justify-center text-accent-600"><i className="ri-time-fill"></i></span>5 min read</span>
            </div>
          </div>
        </header>

        <div className="max-w-[900px] mx-auto px-6 md:px-12 mb-14">
          <div className="relative w-full h-64 md:h-80 lg:h-96 rounded-3xl overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=A%20beautifully%20styled%20flat%20lay%20of%20trending%20fashion%20items%20including%20a%20soft%20pastel%20hoodie%2C%20stylish%20sunglasses%2C%20a%20designer%20tote%20bag%20and%20a%20pair%20of%20clean%20white%20sneakers%20on%20a%20light%20cream%20linen%20surface%2C%20warm%20afternoon%20sunlight%2C%20editorial%20lifestyle%20photography%20with%20warm%20buttery%20tones&width=1200&height=720&seq=blog-trending-hero-v2-035&orientation=landscape"
              alt="Top Kakobuy Spreadsheet Products Trending June 2026"
              title="Kakobuy Spreadsheet Trending Products 2026"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>

        <div className="max-w-[900px] mx-auto px-6 md:px-12 pb-16">
          <div className="prose-custom text-foreground-800 leading-relaxed">
            <section className="mb-12">
              <h2 className="text-foreground-950 text-2xl md:text-3xl font-bold font-heading mb-5">This Month Trending Categories on Kakobuy Spreadsheet</h2>
              <p className="text-foreground-700 text-base leading-relaxed mb-4">
                The <strong>Kakobuy Spreadsheet</strong> tracks product interest across all 14 categories, and June 2026 has shown some clear winners. Lightweight jackets and hoodies are dominating as shoppers prepare for transitional weather, while accessories and bags continue to perform strongly as year-round favorites on KaSpreadsheet.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
                {[
                  { category: 'Hoodies/Sweaters', trend: 'Oversized fits, earth tones, lightweight spring knits' },
                  { category: 'Shoes', trend: 'Clean white sneakers, retro runners, slides' },
                  { category: 'Jackets', trend: 'Bomber jackets, denim jackets, lightweight windbreakers' },
                  { category: 'Accessories', trend: 'Minimal watches, chain necklaces, designer sunglasses' },
                  { category: 'Bags', trend: 'Canvas totes, crossbody bags, sleek backpacks' },
                  { category: 'T-Shirts', trend: 'Premium cotton basics, vintage washes, graphic tees' },
                  { category: 'Sets', trend: 'Coordinated linen sets, matching sweat suits' },
                  { category: 'Perfumes', trend: 'Fresh citrus scents, unisex fragrances, designer dupes' },
                ].map((item, i) => (
                  <div key={i} className="p-5 bg-background-100 rounded-xl">
                    <h3 className="text-foreground-950 font-bold text-sm mb-1">{item.category}</h3>
                    <p className="text-foreground-600 text-xs leading-relaxed">{item.trend}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-foreground-950 text-2xl md:text-3xl font-bold font-heading mb-5">Why These Products Are Trending on Kakobuy Spreadsheet</h2>
              <p className="text-foreground-700 text-base leading-relaxed mb-4">
                Several factors drive product trends within the <strong>Kakobuy Spreadsheet</strong> community. Seasonality plays a major role — June marks the transition from spring to summer in many regions, creating demand for versatile, layerable pieces. Social media influence is another key driver, with viral TikTok and Instagram posts often translating into spikes in specific KaSpreadsheet categories.
              </p>
              <p className="text-foreground-700 text-base leading-relaxed mb-4">
                Price accessibility also matters. The <strong>best Kakobuy Spreadsheet</strong> finds tend to be products that offer exceptional value — high-quality items at prices significantly below retail. This value proposition is what keeps the community engaged and returning weekly for fresh discoveries.
              </p>
              <p className="text-foreground-700 text-base leading-relaxed">
                Finally, seller reliability contributes to trending status. When a verified seller consistently delivers quality products with accurate descriptions and clear QC photos, their listings naturally attract more attention and repeat buyers through the <strong>Kakobuy Spreadsheet</strong>.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-foreground-950 text-2xl md:text-3xl font-bold font-heading mb-5">How to Stay Ahead of Kakobuy Spreadsheet Trends</h2>
              <div className="space-y-3">
                {[
                  'Check the spreadsheet every Monday morning — new weekly updates are pushed at the start of each week.',
                  'Follow the Kakobuy Research Team blog for category-specific trend analysis and buying guides.',
                  'Browse multiple related categories together — for example, Shoes + Accessories for a complete look.',
                  'Act quickly on trending items. Popular products and limited sizes often sell out within days of being listed.',
                  'Share your finds with the community. The more people engage, the better the Kakobuy Spreadsheet becomes for everyone.',
                ].map((tip, i) => (
                  <div key={i} className="flex items-start gap-3 p-4 bg-background-100 rounded-xl">
                    <span className="w-7 h-7 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0"><i className="ri-flashlight-line text-foreground-700 text-sm"></i></span>
                    <p className="text-foreground-700 text-sm leading-relaxed">{tip}</p>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <div className="mt-14 p-8 md:p-10 bg-primary-500 rounded-3xl text-center">
            <h3 className="text-background-50 text-xl md:text-2xl font-bold font-heading mb-3">Discover Trending Products Now</h3>
            <p className="text-background-50/80 text-sm mb-6 max-w-[500px] mx-auto">Explore the hottest finds across all 14 Kakobuy Spreadsheet categories on KaSpreadsheet.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="flex items-center gap-2 h-13 px-8 bg-background-50 text-foreground-950 rounded-full font-semibold text-sm hover:bg-accent-500 transition-all duration-200 cursor-pointer whitespace-nowrap">View Spreadsheet<span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line"></i></span></a>
              <Link to="/" className="flex items-center gap-2 h-13 px-8 border border-background-50/40 text-background-50 rounded-full font-semibold text-sm hover:bg-background-50/15 transition-all duration-200 cursor-pointer whitespace-nowrap">Back to Home<span className="w-4 h-4 flex items-center justify-center"><i className="ri-home-4-line"></i></span></Link>
            </div>
          </div>
        </div>

        <div className="max-w-[900px] mx-auto px-6 md:px-12 pb-16">
          <div className="p-6 bg-background-100 rounded-2xl border border-background-200/70">
            <h4 className="text-foreground-950 font-bold text-sm mb-3">Editorial Policy</h4>
            <p className="text-foreground-500 text-xs leading-relaxed mb-3">Trend data is based on community activity and product interest tracked across the Kakobuy Spreadsheet platform. Individual product availability may vary on KaSpreadsheet.</p>
            <p className="text-foreground-500 text-xs leading-relaxed"><strong>Disclaimer:</strong> Trending products are identified through community engagement metrics. Always verify product details and seller ratings on KaSpreadsheet before purchasing.</p>
          </div>
        </div>

        <div className="max-w-[900px] mx-auto px-6 md:px-12 pb-20 text-center">
          <Link to="/" className="inline-flex items-center gap-2 text-foreground-600 text-sm font-medium hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap">
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line"></i></span>
            Back to Kakobuy Spreadsheet Homepage
          </Link>
        </div>
      </article>
    </>
  );
}